import "./styles/style.scss";

/**
 * Build an embed iframe src for YouTube or Vimeo (autoplay when opened).
 *
 * @param {string} rawUrl
 * @returns {string}
 */
function getEmbedSrc(rawUrl) {
	if (!rawUrl || typeof rawUrl !== "string") {
		return "";
	}
	const urlString = rawUrl.trim();
	if (!urlString) {
		return "";
	}

	let url;
	try {
		url = new URL(urlString);
	} catch {
		return "";
	}

	const host = url.hostname.replace(/^www\./, "");

	if (host === "youtu.be") {
		const id = url.pathname.replace(/^\//, "").split("/")[0];
		return id
			? `https://www.youtube.com/embed/${encodeURIComponent(id)}?autoplay=1&rel=0`
			: "";
	}

	if (host === "youtube.com" || host === "m.youtube.com") {
		if (url.pathname.startsWith("/embed/")) {
			const path = url.pathname + url.search;
			const join = url.search.includes("?") ? "&" : "?";
			return `https://www.youtube.com${path}${join}autoplay=1`;
		}
		const v = url.searchParams.get("v");
		return v
			? `https://www.youtube.com/embed/${encodeURIComponent(v)}?autoplay=1&rel=0`
			: "";
	}

	if (host === "vimeo.com" || host === "player.vimeo.com") {
		const parts = url.pathname.split("/").filter(Boolean);
		const id = parts[parts.length - 1];
		return id && /^\d+$/.test(id)
			? `https://player.vimeo.com/video/${id}?autoplay=1`
			: "";
	}

	return "";
}

/**
 * @param {HTMLElement} root
 */
function closeModal(root) {
	const modal = root.querySelector("[data-kraken-video-lightbox-modal]");
	const iframeWrap = root.querySelector(".kraken-video-lightbox__iframe-wrap");
	const trigger = root.querySelector(".kraken-video-lightbox__trigger");
	if (!modal || !iframeWrap) {
		return;
	}
	modal.hidden = true;
	document.body.classList.remove("kraken-video-lightbox-is-open");
	iframeWrap.innerHTML = "";
	if (trigger && typeof trigger.focus === "function") {
		trigger.focus();
	}
}

/**
 * @param {HTMLElement} root
 */
function bindBlock(root) {
	const trigger = root.querySelector(".kraken-video-lightbox__trigger");
	const modal = root.querySelector("[data-kraken-video-lightbox-modal]");
	const iframeWrap = root.querySelector(".kraken-video-lightbox__iframe-wrap");
	const closeEls = root.querySelectorAll("[data-kraken-video-lightbox-close]");

	if (!trigger || !modal || !iframeWrap) {
		return;
	}

	const videoUrl = root.getAttribute("data-kraken-video-url") || "";

	const open = () => {
		const src = getEmbedSrc(videoUrl);
		if (!src) {
			return;
		}
		const iframe = document.createElement("iframe");
		iframe.setAttribute("src", src);
		iframe.setAttribute("title", "Video");
		iframe.setAttribute(
			"allow",
			"accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share",
		);
		iframe.setAttribute("allowfullscreen", "true");
		iframe.className = "kraken-video-lightbox__iframe";
		iframeWrap.innerHTML = "";
		iframeWrap.appendChild(iframe);
		modal.hidden = false;
		document.body.classList.add("kraken-video-lightbox-is-open");
		const closeBtn = root.querySelector(".kraken-video-lightbox__close");
		if (closeBtn) {
			closeBtn.focus();
		}
	};

	trigger.addEventListener("click", (e) => {
		if (trigger.disabled) {
			return;
		}
		e.preventDefault();
		open();
	});

	closeEls.forEach((el) => {
		el.addEventListener("click", () => {
			closeModal(root);
		});
	});
}

function bindGlobalEscapeOnce() {
	if (document.body.dataset.krakenVideoLightboxEscapeBound === "1") {
		return;
	}
	document.body.dataset.krakenVideoLightboxEscapeBound = "1";
	document.addEventListener("keydown", (e) => {
		if (e.key !== "Escape") {
			return;
		}
		const modal = document.querySelector(
			".wp-block-kraken-core-video-lightbox .kraken-video-lightbox__modal:not([hidden])",
		);
		if (!modal) {
			return;
		}
		const root = modal.closest(".wp-block-kraken-core-video-lightbox");
		if (root instanceof HTMLElement) {
			closeModal(root);
		}
	});
}

function init() {
	bindGlobalEscapeOnce();
	document.querySelectorAll(".wp-block-kraken-core-video-lightbox").forEach((el) => {
		if (!(el instanceof HTMLElement)) {
			return;
		}
		if (el.dataset.krakenVideoLightboxBound === "1") {
			return;
		}
		el.dataset.krakenVideoLightboxBound = "1";
		bindBlock(el);
	});
}

if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", init);
} else {
	init();
}
